import { ReactNode } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Checkbox, Collapse, Stack, Typography, useTheme } from '@mui/material';

import { ResolutionType } from 'src/constants/ResolutionTypes';
import { CareProvided } from 'src/store/sageAdminApi';

export interface ResolutionCategoryItemProps {
    resolutionType: ResolutionType;
    showIcon?: boolean;
    isSelected: boolean;
    disabled?: boolean;
    onSelectOption: (optionId: CareProvided, isChecked: boolean) => void;
    collapseContent?: ReactNode;
}

const ResolutionCategoryItem = ({
    resolutionType,
    showIcon = true,
    isSelected,
    disabled = false,
    onSelectOption,
    collapseContent,
}: ResolutionCategoryItemProps) => {
    const { palette, spacing } = useTheme();

    const isEmergency = resolutionType.category?.id === 'EMERGENCY';

    const iconColor = isEmergency ? palette.error.main : palette.grey[950];

    return (
        <Card
            onClick={() => !disabled && onSelectOption(resolutionType.id, !isSelected)}
            sx={{
                borderColor: isSelected ? palette.primary.main : palette.grey[100],
                borderRadius: 3,
                boxShadow: 0,
                transition: 'outline-color 0.2s',
                outline: isSelected
                    ? `2px solid ${palette.primary.main}`
                    : `1px solid ${palette.grey[100]}`,
                opacity: disabled ? 0.4 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
            }}
        >
            <Stack
                direction="row"
                sx={{ alignItems: 'center', p: 2, justifyContent: 'space-between' }}
            >
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    {showIcon && resolutionType.icon && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: spacing(5),
                                height: spacing(5),
                            }}
                        >
                            <FontAwesomeIcon
                                color={iconColor}
                                icon={resolutionType.icon}
                                size="2x"
                                fixedWidth
                            />
                        </Box>
                    )}
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {resolutionType.displayName}
                    </Typography>
                </Stack>
                <Checkbox
                    onClick={() => onSelectOption(resolutionType.id, !isSelected)}
                    checked={isSelected}
                />
            </Stack>
            {collapseContent && (
                <Collapse in={isSelected} unmountOnExit>
                    {collapseContent}
                </Collapse>
            )}
        </Card>
    );
};

export default ResolutionCategoryItem;
